import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Loader from "~/components/shared/Loader.jsx";
import {
  CustomInput as CustomInputBase,
  DateInput as DateInputBase,
  EmailInput as EmailInputBase,
  FileUploader as FileUploaderBase,
  OptionInput as OptionInputBase,
  PassportInput as PassportInputBase,
  UplaodMenu as UplaodMenuBase,
} from "~/components/ui";
import CountryListBase from "~/components/ui/CountryList.jsx";
import RegexCustomInputBase from "~/components/ui/RegexCustomInput.jsx";

import { useGetUserCountryList } from "../../api/get-user-country-list.api";
import { usersKeys } from "../../api/get-user-information.api";
import { useUpdateProfile } from "../../api/update-user.api";
import { authFormOption, userDefaultValues } from "../../constants";
import { useStatus } from "../../hooks/use-status";
import { authForeingSchema, authSchema } from "../../schemas/auth-form.schema";
import type { AuthFormValues, UserVerificationStatus } from "../../types";
import styles from "./auth-form.module.css";

type LooseUiProps = Record<string, unknown>;
type LooseUiComponent = (props: LooseUiProps) => ReactNode;

const CustomInput = CustomInputBase as unknown as LooseUiComponent;
const DateInput = DateInputBase as unknown as LooseUiComponent;
const EmailInput = EmailInputBase as unknown as LooseUiComponent;
const FileUploader = FileUploaderBase as unknown as LooseUiComponent;
const OptionInput = OptionInputBase as unknown as LooseUiComponent;
const PassportInput = PassportInputBase as unknown as LooseUiComponent;
const UplaodMenu = UplaodMenuBase as unknown as LooseUiComponent;
const CountryList = CountryListBase as unknown as LooseUiComponent;
const RegexCustomInput = RegexCustomInputBase as unknown as LooseUiComponent;

const DOCUMENT_FIELDS = [
  "top_national_card_image",
  "first_birth_certificate_page_image",
  "passport_image",
] as const;

export default function AuthForm() {
  const { status: userStatus } = useStatus();
  const disabled = false;

  const [isLoading, setIsLoading] = useState(true);
  const [validationSchema, setValidationSchema] = useState<typeof authSchema | typeof authForeingSchema>(
    authSchema,
  );
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<AuthFormValues>({
    disabled: disabled,
    defaultValues: userDefaultValues,
    resolver: zodResolver(validationSchema) as never,
  });

  const is_iranian = watch("is_iranian");
  const { data: userCountryList } = useGetUserCountryList();
  const navigate = useNavigate();

  const [activeUplaodMenu, setActiveUplaodMenu] = useState({
    title: "تصویر آواتار",
    value: "profile_picture",
    active: true,
  });
  const { mutateAsync: updateProfile, isPending } = useUpdateProfile();
  const queryClient = useQueryClient();

  const processForm = async (data: AuthFormValues) => {
    try {
      await updateProfile({ data });
      toast.success("مشخصات کاربر با موفقیت ثبت گردید");
      navigate("/auth");
      void queryClient.invalidateQueries({ queryKey: usersKeys.me() });
    } catch (error) {
      console.error("submit user", error);
    }
  };

  useEffect(() => {
    setValidationSchema(is_iranian ? authSchema : authForeingSchema);
    const currentValues = watch();
    reset(currentValues);
  }, [is_iranian]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className={styles.loadingScreen}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.guideCard}>
        <img className={styles.guideImage} src="/images/vandawelcomepic.png" alt="welcome-pic" />
        <div className={styles.guideBody}>
          <p className={styles.guideTitle}>راهنما</p>
          <p className={styles.guideText}>
            در صورتی که به صورت حقیقی (یا حقوقی) در وندا پرداخت ثبت کسب‌ و کار می‌کنید، شبای شما باید متصل به
            حساب کسب‌ و کار حقیقی (یا حقوقی) شما باشد. طبق اعلامیه شاپرک، صاحب حسابی که شماره‌شبا آن راوارد
            می‌نمایید باید با دارنده کدملی و مالک شماره تلفن همراه یکسان باشد.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(processForm)}
        className={`${styles.form} ${userStatus === ("approved" as UserVerificationStatus) ? styles.formApproved : ""}`}
      >
        <div className={styles.sectionHeader}>
          <p className="text-title-lg-semibold">اطلاعات شخصی</p>
          <hr />
        </div>
        {!disabled && (
          <OptionInput
            reset={reset}
            control={control}
            register={register}
            name="is_iranian"
            options={authFormOption}
            errors={errors.is_iranian}
            className={`${styles.order1} ${styles.fullWidth} ${styles.span2}`}
          />
        )}

        <RegexCustomInput
          tabIndex="1"
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name="first_name_fa"
          label="نام (فارسی)*"
          placeholder="نام به فارسی"
          type="text"
          errors={errors.first_name_fa}
          className={styles.order1}
          regexPattern={/^[\u0600-\u06FF\s]+$/}
          errorMessage="فقط کاراکتر فارسی مجاز است"
        />
        <RegexCustomInput
          tabIndex="4"
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name="first_name_en"
          label="نام (انگلیسی)*"
          placeholder="نام به انگلیسی"
          type="text"
          errors={errors.first_name_en}
          className={`${styles.order4} ${styles.order4Md2}`}
          regexPattern={/^[a-zA-Z?><;,{}[\]\-_+=!@#$%^&*|' ]*$/}
          errorMessage="فقط کاراکتر انگلیسی مجاز است"
        />
        <RegexCustomInput
          tabIndex="2"
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name="last_name_fa"
          label="نام خانوادگی (فارسی)*"
          placeholder="نام خانوادگی به فارسی"
          type="text"
          errors={errors.last_name_fa}
          className={`${styles.order2} ${styles.order2Md3}`}
          regexPattern={/^[\u0600-\u06FF\s]+$/}
          errorMessage="فقط کاراکتر فارسی مجاز است"
        />
        <RegexCustomInput
          tabIndex="5"
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name="last_name_en"
          label="نام خانوادگی (انگلیسی)*"
          placeholder="نام خانوادگی به انگلیسی"
          type="text"
          errors={errors.last_name_en}
          className={`${styles.order5} ${styles.order5Md4}`}
          regexPattern={/^[a-zA-Z?><;,{}[\]\-_+=!@#$%^&*|' ]*$/}
          errorMessage="فقط کاراکتر انگلیسی مجاز است"
        />
        <RegexCustomInput
          tabIndex="3"
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name="father_name_fa"
          label="نام پدر (فارسی)*"
          placeholder="نام پدر به فارسی"
          type="text"
          errors={errors.father_name_fa}
          className={`${styles.order3} ${styles.order3Md5}`}
          regexPattern={/^[\u0600-\u06FF\s]+$/}
          errorMessage="فقط کاراکتر فارسی مجاز است"
        />
        <RegexCustomInput
          tabIndex="6"
          control={control}
          disabled={disabled}
          setValue={setValue}
          setError={setError}
          clearErrors={clearErrors}
          name="father_name_en"
          label="نام پدر (انگلیسی)*"
          placeholder="نام پدر به انگلیسی"
          type="text"
          errors={errors.father_name_en}
          className={styles.order6}
          regexPattern={/^[a-zA-Z?><;,{}[\]\-_+=!@#$%^&*|' ]*$/}
          errorMessage="فقط کاراکتر انگلیسی مجاز است"
        />

        {watch("is_iranian") ? (
          <CustomInput
            tabIndex="7"
            control={control}
            disabled={disabled}
            name="national_code"
            label=" کدملی"
            placeholder="کد ۱۰ رقمی"
            type="number"
            className={styles.orderLast}
            errors={errors.national_code}
          />
        ) : (
          <CustomInput
            tabIndex="8"
            control={control}
            disabled={disabled}
            name="foreign_pervasive_code"
            placeholder="کد ۱۲ رقمی"
            label=" کد فراگیر"
            className={styles.orderLast}
            type="number"
            errors={errors.foreign_pervasive_code}
          />
        )}
        {!watch("is_iranian") && (
          <CountryList
            control={control}
            register={register}
            name="countrycode_id"
            label="کشور"
            className={styles.orderLast}
            errors={errors.countrycode_id}
            options={userCountryList?.items || []}
          />
        )}
        <DateInput
          control={control}
          name="birthdate"
          label="تاریخ تولد"
          placeholder="روز/ماه/سال"
          type="text"
          className={styles.orderLast}
          errors={errors.birthdate}
          maxDate={Date.now()}
        />
        {!watch("is_iranian") && (
          <DateInput
            control={control}
            register={register}
            name="passport_expire_date"
            label="تاریخ اعتبار گذرنامه"
            placeholder="روز/ماه/سال"
            type="text"
            errors={errors.passport_expire_date}
            className={styles.orderLast}
          />
        )}

        {!watch("is_iranian") && (
          <PassportInput
            control={control}
            register={register}
            name="passport_number"
            placeholder="۸ عدد بعد از حرف"
            className={styles.orderLast}
            label="شماره گذرنامه"
            type="number"
            errors={errors.passport_number}
          />
        )}

        <EmailInput
          control={control}
          name="email"
          label="ایمیل"
          type="email"
          errors={errors}
          className={`${styles.orderLast} ${styles.span2}`}
        />

        <div className={`${styles.uploads} ${styles.orderLast}`}>
          <UplaodMenu setActiveUplaodMenu={setActiveUplaodMenu} type="auth" value={is_iranian} />
          {activeUplaodMenu.value === "profile_picture" && (
            <Controller
              control={control}
              name="profile_picture"
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  emptyAvatar={true}
                  fieldChange={onChange}
                  pic_type="USER_PROFILE"
                  mediaUrl={value}
                  disable={userStatus === "approved"}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}
          {activeUplaodMenu.value === "top_national_card_image" && (
            <Controller
              control={control}
              name="top_national_card_image"
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  emptyAvatar={true}
                  pic_type="TOP_NATIONAL_CARD"
                  fieldChange={onChange}
                  mediaUrl={value}
                  disable={userStatus === "approved"}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}
          {activeUplaodMenu.value === "first_birth_certificate_page_image" && (
            <Controller
              control={control}
              name="first_birth_certificate_page_image"
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  pic_type="FIRST_BIRTH_CERTIFICATE_PAGE"
                  emptyAvatar={true}
                  fieldChange={onChange}
                  mediaUrl={value}
                  disable={userStatus === "approved"}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}
          {activeUplaodMenu.value === "passport_image" && (
            <Controller
              control={control}
              name="passport_image"
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  pic_type="PASSPORT"
                  emptyAvatar={true}
                  fieldChange={onChange}
                  mediaUrl={value}
                  disable={userStatus === "approved"}
                  acceptedFormats={[".png", ".jpeg", ".jpg", ".webp"]}
                />
              )}
            />
          )}

          {DOCUMENT_FIELDS.map((field) =>
            errors[field]?.preview_url?.message ? (
              <span key={field} className={styles.fieldError}>
                {errors[field]?.preview_url?.message}
              </span>
            ) : null,
          )}
        </div>

        <button
          disabled={disabled || isPending}
          type="submit"
          className={`btn-container disabled:bg-slate-300 disabled:pointer-events-none ${styles.orderLast} ${styles.submit}`}
        >
          {isPending ? (
            <div className={styles.submitInner}>
              <p className={styles.submitHint}>در حال ارسال</p>
              <div className={styles.loaderBox}>
                <Loader />
              </div>
            </div>
          ) : userStatus === "pending" ? (
            <p> ویرایش اطلاعات</p>
          ) : (
            <p> ثبت اطلاعات هویتی</p>
          )}
        </button>
      </form>
    </div>
  );
}
